'use client';

import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { clientLogger } from '@/utils/clientLogger';
import { getMnoConfig } from './mno/configs';
import { MnoConfig } from './mno/types';

/**
 * MNO Context Value - provides MNO configuration throughout the app
 */
interface MnoContextValue {
  config: MnoConfig;
  isLoading: boolean;
  error?: Error;
}

/**
 * MNO Context - holds the current MNO configuration
 */
const MnoContext = createContext<MnoContextValue | undefined>(undefined);

/**
 * MNO Provider Props
 */
interface MnoProviderProps {
  children: ReactNode;
}

/**
 * MnoProvider - Provides MNO configuration to the entire app
 * This should be placed high in the component tree (e.g., in layout.tsx)
 * MNO is determined from the URL subdomain
 */
export function MnoProvider({ children }: MnoProviderProps) {
  // Load MNO config once at the provider level based on subdomain
  const value = useMemo<MnoContextValue>(() => {
    try {
      const config = getMnoConfig();
      return {
        config,
        isLoading: false,
        error: undefined,
      };
    } catch (error) {
      clientLogger.error('Failed to load MNO configuration:', { error });
      // Return default config in case of error - getMnoConfig will handle fallback
      return {
        config: getMnoConfig(), // fallback to default
        isLoading: false,
        error: error instanceof Error ? error : new Error('Unknown error loading MNO config'),
      };
    }
  }, []);

  return <MnoContext.Provider value={value}>{children}</MnoContext.Provider>;
}

/**
 * useMnoContext - Hook to access MNO context
 * Must be used within MnoProvider
 * 
 * @throws Error if used outside MnoProvider
 */
export function useMnoContext(): MnoContextValue {
  const context = useContext(MnoContext);
  
  if (context === undefined) {
    throw new Error(
      'useMnoContext must be used within MnoProvider. ' +
      'Make sure your component is wrapped with <MnoProvider>.'
    );
  }
  
  return context;
}

